// A set of the commissioned terrain from its two files (docs/166 B): the JSON record and the atlas PNG,
// decoded to RGBA so the painter can read every piece's pixels. No Phaser and no build-time imports here:
// the board painter's Web Worker (paint.worker.ts) loads its sets through this too.
import type { EncounterSet, Piece, SetRecord } from './types';

/** Where a set's two files are served (the build's `virtual:fk-encounter-atlas` entry for it). */
export interface SetUrls { json: string; png: string }

/** Where each set's frames sit in its atlas (the record's `files`, which the set itself does not carry). */
const positions = new WeakMap<EncounterSet, Map<string, { x: number; y: number; w: number; h: number }>>();

/** A file's frame in the set's texture: `floor_01`, `torch_02_f1`. */
export function frameName(piece: string, v: number, f?: number): string {
  return `${piece}_${String(v).padStart(2, '0')}${f !== undefined ? `_f${f}` : ''}`;
}

/** Every frame of a set made by `setFrom`, by name, as a rectangle of its atlas. */
export function framesOf(set: EncounterSet): ReadonlyMap<string, { x: number; y: number; w: number; h: number }> | undefined {
  return positions.get(set);
}

/** A set's two files fetched (ahead of the page's other loads: a fight waits on them) and the atlas decoded to RGBA. */
export async function fetchSetFiles(urls: SetUrls): Promise<{ record: SetRecord; data: Uint8ClampedArray; width: number }> {
  const [record, blob] = await Promise.all([
    fetch(urls.json, { priority: 'high' }).then((r) => { if (!r.ok) throw new Error(`${urls.json}: ${r.status}`); return r.json() as Promise<SetRecord>; }),
    fetch(urls.png, { priority: 'high' }).then((r) => { if (!r.ok) throw new Error(`${urls.png}: ${r.status}`); return r.blob(); }),
  ]);
  // no premultiplying or colour management: the art's pixels are the palette's own
  const image = await createImageBitmap(blob, { premultiplyAlpha: 'none', colorSpaceConversion: 'none' });
  const { data, width } = rgbaOf(image);
  image.close?.();
  return { record, data, width };
}

/** Fetches and decodes a set. */
export async function fetchSetFrom(urls: SetUrls): Promise<EncounterSet> {
  const { record, data, width } = await fetchSetFiles(urls);
  return setFrom(record, data, width);
}

function rgbaOf(image: ImageBitmap): { data: Uint8ClampedArray; width: number } {
  const { width, height } = image;
  if (typeof OffscreenCanvas !== 'undefined') {
    const ctx = new OffscreenCanvas(width, height).getContext('2d', { willReadFrequently: true });
    if (ctx) { ctx.drawImage(image, 0, 0); return { data: ctx.getImageData(0, 0, width, height).data, width }; }
  }
  const canvas = document.createElement('canvas');
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no 2d canvas to read the encounter atlas');
  ctx.drawImage(image, 0, 0);
  return { data: ctx.getImageData(0, 0, width, height).data, width };
}

/** An `EncounterSet` from a record and its atlas's RGBA (`width` px a row): every file's pixels cut out, by piece, in number order. */
export function setFrom(record: SetRecord, data: Uint8ClampedArray | Uint8Array, width: number, image?: TexImageSource): EncounterSet {
  const pieces = new Map<string, Piece[]>();
  const at = new Map<string, { x: number; y: number; w: number; h: number }>();
  for (const f of record.files) {
    const px = new Uint8ClampedArray(f.w * f.h * 4);
    for (let r = 0; r < f.h; r++) {
      const o = ((f.y + r) * width + f.x) * 4;
      px.set(data.subarray(o, o + f.w * 4), r * f.w * 4);
    }
    const piece: Piece = { piece: f.piece, v: f.v, w: f.w, h: f.h, px, frame: frameName(f.piece, f.v, f.f), ...(f.f !== undefined ? { f: f.f } : {}) };
    at.set(piece.frame, { x: f.x, y: f.y, w: f.w, h: f.h });
    let list = pieces.get(f.piece);
    if (!list) pieces.set(f.piece, (list = []));
    list.push(piece);
  }
  for (const list of pieces.values()) list.sort((a, b) => a.v - b.v || (a.f ?? 0) - (b.f ?? 0));
  const { files, ...rest } = record;
  void files;
  const rgba = data instanceof Uint8ClampedArray ? data : new Uint8ClampedArray(data.buffer, data.byteOffset, data.length);
  const set: EncounterSet = { ...rest, pieces, texture: '', atlas: { rgba, width, height: Math.floor(rgba.length / 4 / Math.max(1, width)) }, ...(image ? { image } : {}) };
  positions.set(set, at);
  return set;
}
